import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WagerPanel from "@/components/WagerPanel";
import { toast } from "sonner";
import { Brain, RotateCcw } from "lucide-react";
import { maybeCelebrate } from "@/lib/celebrate";
import { playSfx } from "@/lib/sfx";

const SYMBOLS = ["♠", "♥", "♦", "♣", "★", "◆", "▲", "●"];

function makeDeck() {
  const arr = [...SYMBOLS, ...SYMBOLS].map((s, i) => ({ id: i, sym: s, matched: false }));
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const TIME_LIMIT = 90;

export default function MemoryGame() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [session, setSession] = useState(null); // {session_id, wager}
  const [deck, setDeck] = useState([]);
  const [flipped, setFlipped] = useState([]); // indexes
  const [matched, setMatched] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(TIME_LIMIT);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {payout, result}
  const timerRef = useRef(null);

  const totalPairs = SYMBOLS.length;

  useEffect(() => {
    if (!session || result) return;
    timerRef.current = setInterval(() => setTime((t) => t - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [session, result]);

  useEffect(() => {
    if (time <= 0 && session && !result) settle();
    // eslint-disable-next-line
  }, [time]);

  useEffect(() => {
    if (matched.size === totalPairs && session && !result) settle();
    // eslint-disable-next-line
  }, [matched]);

  const start = async (wager) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/wager", { game: "memory", wager });
      setSession(data);
      setDeck(makeDeck());
      setMatched(new Set());
      setFlipped([]);
      setMoves(0);
      setTime(TIME_LIMIT);
      setResult(null);
      playSfx("wagerConfirm");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start");
    } finally { setBusy(false); }
  };

  const flip = (i) => {
    if (result || flipped.length >= 2 || flipped.includes(i) || matched.has(deck[i].sym)) return;
    const nf = [...flipped, i];
    setFlipped(nf);
    playSfx("flip");
    if (nf.length === 2) {
      setMoves((m) => m + 1);
      if (deck[nf[0]].sym === deck[nf[1]].sym) {
        setMatched((m) => new Set(m).add(deck[nf[0]].sym));
        playSfx("match");
        setTimeout(() => setFlipped([]), 400);
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  const settle = async () => {
    clearInterval(timerRef.current);
    setBusy(true);
    try {
      const { data } = await api.post("/games/settle", {
        session_id: session.session_id,
        outcome: {
          matched: matched.size,
          total_pairs: totalPairs,
          time_used: TIME_LIMIT - time,
          time_limit: TIME_LIMIT,
          moves,
        },
      });
      setResult(data);
      if (data.payout > 0) {
        toast.success(`+${data.payout.toLocaleString()} chips`);
        maybeCelebrate({ payout: data.payout, wager: session.wager });
        playSfx("cashOut");
        if (data.payout >= session.wager * 3) setTimeout(() => playSfx("megaWin"), 200);
      }
      else { toast.error(`No payout · lost ${session.wager}`); playSfx("lose"); }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Settle failed");
    } finally { setBusy(false); }
  };

  const restart = () => { setSession(null); setResult(null); };

  return (
    <div className="space-y-6" data-testid="memory-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><Brain className="w-3.5 h-3.5" /> Memory Matrix</div>
          <h1 className="font-display text-3xl mt-2">Pair the deck</h1>
        </div>
        <button onClick={() => nav("/lobby")} className="btn-ghost text-sm" data-testid="back-to-lobby">Back to lobby</button>
      </div>

      {!session ? (
        <WagerPanel game="Memory" onStart={start} busy={busy} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className={`vexis-card p-4 sm:p-6 ${result ? (result.payout > 0 ? "win-glow" : "loss-glow") : ""}`}>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              {deck.map((c, i) => {
                const isFlipped = flipped.includes(i) || matched.has(c.sym);
                const isMatched = matched.has(c.sym);
                return (
                  <div key={c.id} className={`mem ${isFlipped ? "flipped" : ""} ${isMatched ? "matched" : ""}`} onClick={() => flip(i)} data-testid={`memory-card-${i}`}>
                    <div className="mem-inner">
                      <div className="mem-face mem-back" />
                      <div className="mem-face mem-front">{c.sym}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <div className="vexis-card p-4">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Timer</div>
              <div className={`font-display text-4xl ${time <= 10 ? "text-red-400" : "text-slate-100"}`} data-testid="memory-timer">{time}s</div>
            </div>
            <div className="vexis-card p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-slate-500 text-xs font-mono uppercase">Pairs</div><div className="font-display text-xl">{matched.size}/{totalPairs}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Moves</div><div className="font-display text-xl">{moves}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Wager</div><div className="font-display text-xl">{session.wager}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Potential</div><div className="font-display text-xl text-[color:var(--gold)]">1.8×–3×</div></div>
              </div>
            </div>
            {result && (
              <div className={`vexis-card p-4 ${result.payout > 0 ? "win-glow" : "loss-glow"}`} data-testid="memory-result">
                {result.payout >= session.wager * 2 && (
                  <div className="pill pill-gold mb-2 inline-flex" data-testid="memory-mega">✦ Mega Win · {(result.payout / session.wager).toFixed(2)}×</div>
                )}
                <div className={`font-display text-2xl ${result.payout > 0 ? "text-emerald-400" : "text-red-400"}`}>{result.payout > 0 ? "Won" : "No payout"}</div>
                <div className="text-sm mt-1 text-slate-300">Payout · <span className="font-mono">{result.payout}</span> chips</div>
                <button onClick={restart} className="btn-gold w-full mt-4 inline-flex justify-center items-center gap-2" data-testid="memory-play-again"><RotateCcw className="w-4 h-4" /> Play again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
